/**
 * Updates the recently viewed products in localStorage.
 * Stores product handles and IDs for Section Rendering API and predictive search.
 */

/**
 * @typedef {Object} ViewedProductEntry
 * @property {string} handle - Product URL slug
 * @property {string | null} id - Shopify product ID (numeric string)
 */

export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Normalizes stored entries, migrating legacy handle-only strings.
   * @returns {ViewedProductEntry[]}
   */
  static #getEntries() {
    /** @type {unknown[]} */
    const raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');

    return raw
      .map((/** @type {unknown} */ entry) => {
        if (typeof entry === 'string') {
          return { handle: entry, id: null };
        }

        if (entry && typeof entry === 'object' && 'handle' in entry) {
          const { handle, id } = /** @type {{ handle?: string; id?: string | number | null }} */ (entry);
          return {
            handle: handle || '',
            id: id != null && id !== '' ? String(id) : null,
          };
        }

        return null;
      })
      .filter((/** @type {ViewedProductEntry | null} */ entry) => entry != null && entry.handle !== '');
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productHandle - The handle (URL slug) of the product to add.
   * @param {string | number} productId - The Shopify product ID.
   */
  static addProduct(productHandle, productId) {
    let entries = this.#getEntries();

    entries = entries.filter((entry) => entry.handle !== productHandle);
    entries.unshift({ handle: productHandle, id: String(productId) });
    entries = entries.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(entries));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed product handles from localStorage.
   * @returns {string[]} The list of viewed product handles.
   */
  static getProducts() {
    return this.#getEntries().map((entry) => entry.handle);
  }

  /**
   * Retrieves numeric product IDs for predictive search (id: queries).
   * Legacy handle-only entries are omitted until the product is viewed again.
   * @returns {string[]}
   */
  static getProductIds() {
    return this.#getEntries()
      .map((entry) => entry.id)
      .filter((/** @type {string | null} */ id) => id != null && id !== '');
  }
}
